import {
    MoneyOffRounded as MoneyOffRoundedIcon,
    WarningAmberRounded as WarningAmberRoundedIcon,
} from 'components/icons';
import Chip from 'components/@extended/Chip.tsx';
import ListCreateButton from 'components/buttons/ListCreateButton';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftRowMenu } from 'components/drafts/DraftRecordActions';
import { useWithDrafts } from 'components/drafts/useWithDrafts';
import Loader from 'components/Loader';
import { FormattedDate } from 'components/misc/FormattedDate';
import LineClamp from 'components/misc/LineClamp.tsx';
import ExpenseModal from 'components/modals/expenses/ExpenseModal';
import { useModal } from 'components/modals/useModal';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { mapToFilterOptions } from 'components/tables/list-table/filterUtil.ts';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { Checkbox } from 'components/ui/checkbox';
import { Tooltip } from 'components/ui/tooltip';
import { VIEW_PRESERVED_PARAM_KEYS } from 'utils/viewParam';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import MatterIndicators, { BOTTOM_LINE_IN_NEW_DESIGN, hasMatterAccess } from 'components/matters/MatterIndicators';
import MoneyDisplay from 'components/util/MoneyDisplay';
import useAuth from 'hooks/useAuth.ts';
import isEmpty from 'lodash/isEmpty';
import ExpenseActionButtons from 'pages/matters/expenses/ExpenseActionButtons';
import { getLinks } from 'pages/matters/util';
import TimecardLinkIcon from 'pages/timecards/expenses/TimeCardLinkIcon';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import {
    useAddExpenseMutation,
    useGetExpensesTotalsQuery,
    useLazyGetExpensesCountQuery,
    useLazyGetExpensesWithFiltersQuery,
} from 'store/api/expenses';
import { useStartExpenseExportMutation } from 'store/api/exports';
import { useGetCurrentTenantQuery } from 'store/api/tenants';
import { EmployeeListItemMinDto } from 'types/employees';
import { ExpenseCreateDto, ExpenseForm, ExpenseListItem, ExpensesTotalsDto, ExpenseStatusType } from 'types/expenses';
import { EXPORT_CONFIG } from 'types/exports';
import { FeatureFlag } from 'types/features.ts';
import { MatterDto, MatterStateType } from 'types/matters.ts';
import { errorAlert } from 'utils/alert';
import { PaginatedFieldPassiveFilter } from 'utils/filterUtil.ts';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { displayName, useLastNameFirst } from 'utils/nameOrder';
import { useHasPermissions } from 'utils/permissionUtil';
import { useAsyncExport } from 'utils/useAsyncExport.ts';
import { useClassifiers } from 'utils/useClassifiers';
import { useExpenseEntryDisabled } from 'utils/useExpenseEntryDisabled';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import {
    buildExpensesFilterConfig,
    EXPENSES_FILTER_STATUS_TYPES,
    ExpensesFilterGroup,
    ExpensesFilterId,
} from './expensesFilterConfig';
import { getLatestConnectedInvoicePaidDate } from './expensesInvoiceUtil';
import ExpensesTableActions from './ExpensesTableActions.tsx';
import { ExpenseStatusLabel } from './ExpenseStatusLabel';

/** A draft is invoiced, as everything else, once it is created. */
export function isExpenseSelectable(expense: ExpenseListItem) {
    return !expense.isDraft && expense.expenseStatusTypeCode === ExpenseStatusType.FINISHED;
}

/** The list's count, with the drafts it lists. */
const useLazyExpensesCountWithDraftsQuery: typeof useLazyGetExpensesCountQuery = (options) => {
    const [count, ...rest] = useLazyGetExpensesCountQuery(options);
    return [useWithDrafts(count), ...rest];
};

const USED_CLASSIFIERS = ['EXPENSE_TYPE', 'EXPENSE_STATUS_TYPE', 'CURRENCY', 'INVOICE_STATUS_TYPE'];
const EMPTY_FILTER = '';

/** A link in a cell: the cell's `.entity-link` rule colours it, and it underlines on hover. */
const CELL_LINK = 'entity-link w-fit no-underline hover:underline';

/** An expenses column, with the totals type pinned so `meta.renderTotal` takes the concrete DTO. */
type ExpenseColumn = DataTableColumnDef<ExpenseListItem, ExpensesTotalsDto | undefined>;

/**
 * Totals-row cell for the Amount column, in the tenant base currency. Currencies the tenant's rate
 * source doesn't quote reach no total, so the figure carries a warning listing them — unannotated it
 * would read as covering the whole filtered set. Shows "-" when nothing could be converted.
 *
 * Stays inline-level: the managed layout measures each totals cell with a Range over its contents, so
 * a full-width block would report the column's current width as its content floor and ratchet that
 * floor on every resolve, leaving the column unable to shrink again.
 */
const renderAmountTotal = (totals: ExpensesTotalsDto | undefined): React.ReactNode => {
    if (!totals) {
        return null;
    }
    const { totalAmountInBaseCurrency, baseCurrencyTypeCode, excludedCurrencies } = totals;
    return (
        <span className="inline-flex items-center gap-1">
            {totalAmountInBaseCurrency != null ? (
                <MoneyDisplay amount={totalAmountInBaseCurrency} currency={baseCurrencyTypeCode} />
            ) : (
                '-'
            )}
            {excludedCurrencies.length > 0 ? (
                <Tooltip
                    content={t('expenses.totals.excluded-currencies', {
                        currencies: excludedCurrencies.join(', '),
                        baseCurrency: baseCurrencyTypeCode,
                    })}
                >
                    <WarningAmberRoundedIcon className="text-fg-secondary text-[16px]" />
                </Tooltip>
            ) : null}
        </span>
    );
};

const getExpenseHref = (expense: ExpenseListItem) => `/expense/${expense.id}`;

const ExpensesTable = ({
    matter,
    passiveFilter = EMPTY_FILTER,
    disableExport = false,
    disabledGroups,
    disabledFilterIds,
    paginatedFieldPassiveFilters,
    canRememberFilters = true,
    extraHeaderComponent,
    quickFilterTrailing,
}: {
    matter?: MatterDto;
    passiveFilter?: string;
    disableExport?: boolean;
    disabledGroups?: ExpensesFilterGroup[];
    disabledFilterIds?: ExpensesFilterId[];
    paginatedFieldPassiveFilters?: PaginatedFieldPassiveFilter<ExpensesFilterId>[];
    canRememberFilters?: boolean;
    extraHeaderComponent?: React.ReactNode;
    quickFilterTrailing?: React.ReactNode;
}): JSX.Element => {
    const [fetchExpenses, { data, isFetching }] = useLazyGetExpensesWithFiltersQuery();
    const fetchData = useWithDrafts(fetchExpenses);
    const [addExpense] = useAddExpenseMutation();
    const isCaseClosed =
        matter?.matterStateTypeCode === MatterStateType.COMPLETED ||
        matter?.matterStateTypeCode === MatterStateType.OFFER_LOST ||
        matter?.matterStateTypeCode === MatterStateType.OFFER_WON;

    const isClientPersonaNonGrata = matter?.clientParty?.isPersonaNonGrata === true;

    const hasPermissionToExportExpenses = useHasPermissions(['EXPORT.EXPENSES']);
    const isExpenseEntryDisabled = useExpenseEntryDisabled();
    const isSortingEnabled = useIsFeatureEnabled(FeatureFlag.SORTING_INVOICES);

    const addExpenseDisabledTooltipText = isExpenseEntryDisabled
        ? t('expenses.integration-entry-disabled')
        : isClientPersonaNonGrata
          ? t('contacts.tooltips.add-new-case-disabled-persona-non-grata')
          : undefined;

    const { user } = useAuth();
    const userId = user?.id;
    // The columns memo is not a component, so read the flag here and thread it into the closure.
    const lastNameFirst = useLastNameFirst();
    const { openModal, closeModal } = useModal();
    const navigateWithReturn = useNavigateWithReturnState();
    const [selectedIds, setSelectedIds] = useState(new Set<number>());
    const finishedItems = useMemo(() => data?.content?.filter(isExpenseSelectable) || [], [data]);
    const anyToSelect = finishedItems.length > 0;
    const canSelectExpenses = matter !== undefined;
    // A case tab shares one case across every row, so its indicators live in the case page header.
    const isCaseScoped = matter !== undefined;

    const { data: currentEmployeeQueryData, isLoading: isLoadingCurrentEmployeeQueryData } =
        useGetEmployeesListWithFiltersQuery({ pageSize: 1, page: 0, filters: `id==${userId}` }, { skip: !userId });

    const currentEmployeeData = useMemo<EmployeeListItemMinDto | undefined>(() => {
        return currentEmployeeQueryData?.content.at(0);
    }, [currentEmployeeQueryData]);

    const { data: tenant, isLoading: isLoadingTenant } = useGetCurrentTenantQuery();

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: USED_CLASSIFIERS,
    });

    const { isBusy: isExportDataFetching, triggerManualDownload } = useAsyncExport(useStartExpenseExportMutation, {
        entityType: 'EXPENSE',
        entityName: t('expenses.export.entity-name'),
        fileName: EXPORT_CONFIG.EXPENSE.fileName,
    });

    const handleExpenseCreate = useCallback(
        async (expense: ExpenseCreateDto) => {
            try {
                await addExpense({ ...expense }).unwrap();

                return 0;
            } catch (e) {
                console.error('onSubmit error: ', e);
                errorAlert(t('forms.error-submitting-the-form'));
            }
        },
        [addExpense]
    );

    // The matter-embedded "Add expense" affordance opens the expense's create form with the case prefilled: in
    // the create dialog where there is one, on the standalone create page elsewhere. Inline-modal create
    // remains only for duplicate.
    const openCreateExpensePage = useCallback(() => {
        navigateWithReturn(
            '/expense/new',
            matter
                ? {
                      state: {
                          presetMatter: {
                              id: matter.id,
                              name: matter.name,
                              fullName: matter.fullName,
                              isNonBillable: matter.isNonBillable,
                              isConfidential: matter.isConfidential,
                          },
                      },
                  }
                : undefined
        );
    }, [navigateWithReturn, matter]);

    const handleDuplicateExpense = useCallback(
        (expenseToDuplicate: ExpenseListItem): void => {
            if (isExpenseEntryDisabled) {
                return;
            }
            const preSelectedEmployee = currentEmployeeData;

            const initialData: ExpenseForm = {
                id: undefined,
                externalId: null,
                expenseType:
                    (
                        getOptionsFromClassifier(classifiers['EXPENSE_TYPE']) as {
                            label: string;
                            value: string;
                        }[]
                    ).find((val) => val.value === expenseToDuplicate.expenseTypeCode) ?? null,
                currencyType:
                    (
                        getOptionsFromClassifier(classifiers['CURRENCY']) as {
                            label: string;
                            value: string;
                        }[]
                    ).find((val) => val.value === expenseToDuplicate.originalCurrencyTypeCode) ?? null,
                description: expenseToDuplicate.description ?? '',
                expenseAmount: expenseToDuplicate.expenseAmountOriginalCurrency?.toString() ?? '',
                expenseDate: expenseToDuplicate.expenseDate
                    ? formatDateStringInput(new Date(expenseToDuplicate.expenseDate))
                    : formatDateStringInput(new Date()),
                isNonBillable: expenseToDuplicate.isNonBillable ?? false,
                employee: expenseToDuplicate.employee
                    ? expenseToDuplicate.employee
                    : preSelectedEmployee
                      ? { id: preSelectedEmployee.id, name: preSelectedEmployee.name }
                      : null,
                matter: null,
                effortId: null,
                timecardId: null,
            };

            openModal(
                <ExpenseModal
                    onChange={(value) => {
                        void handleExpenseCreate(value);
                    }}
                    initialData={initialData}
                    onClose={closeModal}
                    titleId="expenses.duplicate-expense"
                    readOnly={isExpenseEntryDisabled}
                    preselectMatterId={expenseToDuplicate.matter?.id || matter?.id}
                    hideSelectMatter={false}
                    tenant={tenant}
                />,
                closeModal
            );
        },
        [
            classifiers,
            currentEmployeeData,
            closeModal,
            openModal,
            matter,
            handleExpenseCreate,
            tenant,
            isExpenseEntryDisabled,
        ]
    );

    const columns = useMemo<ExpenseColumn[]>(() => {
        const actionsCell: ExpenseColumn[] =
            canSelectExpenses && anyToSelect
                ? [
                      {
                          header: () => {
                              const allSelected = selectedIds.size === finishedItems.length;
                              return (
                                  <Checkbox
                                      id="select-all"
                                      variant="secondary"
                                      aria-label={t('forms.select-all')}
                                      checked={allSelected}
                                      onCheckedChange={(checked) => {
                                          if (checked) {
                                              setSelectedIds(new Set(finishedItems.map((row) => row.id)));
                                          } else {
                                              setSelectedIds(new Set());
                                          }
                                      }}
                                  />
                              );
                          },
                          enableSorting: false,
                          id: 'item-select',
                          meta: {
                              fixedPx: 64,
                          },
                          cell: ({ row }) => {
                              const expenseListItem = row.original;
                              return isExpenseSelectable(expenseListItem) ? (
                                  <Checkbox
                                      id={expenseListItem.id.toString()}
                                      variant="secondary"
                                      aria-label={t('invoices.tooltips.bulk-selection')}
                                      checked={selectedIds.has(expenseListItem.id)}
                                      onCheckedChange={(checked) => {
                                          const newSelection = new Set(selectedIds);
                                          if (checked) {
                                              newSelection.add(expenseListItem.id);
                                          } else {
                                              newSelection.delete(expenseListItem.id);
                                          }
                                          setSelectedIds(newSelection);
                                      }}
                                  />
                              ) : null;
                          },
                      },
                  ]
                : [];
        return [
            ...actionsCell,
            {
                header: t('expenses.date'),
                id: 'expenseDate',
                accessorKey: 'expenseDate',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'expenseDate',
                    widthWeight: 1,
                    // The nowrap date link needs 91px + the cell's 32px of horizontal padding. A
                    // managed cell clips rather than bleeding into the next one, so the floor has to
                    // cover the content.
                    minPx: 124,
                },
                cell: ({ cell }) => {
                    const expense = cell.row.original;
                    return (
                        <div className="flex h-[49px] items-center whitespace-nowrap">
                            <LinkWithOrigin
                                className={cn(CELL_LINK, 'text-left whitespace-nowrap')}
                                to={`/expense/${expense.id}`}
                            >
                                <FormattedDate value={expense.expenseDate} />
                            </LinkWithOrigin>
                        </div>
                    );
                },
            },
            {
                header: t('expenses.type-description'),
                id: 'description',
                accessorKey: 'description',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'expenseTypeCode',
                    widthWeight: 1.72,
                    minPx: 150,
                },
                cell: ({ cell }) => {
                    const { description, expenseTypeCode } = cell.row.original;
                    return (
                        <div className="flex flex-col gap-1">
                            <LinkWithOrigin
                                className={cn(CELL_LINK, 'whitespace-nowrap')}
                                to={`/expense/${cell.row.original.id}`}
                            >
                                <p className="text-body1 font-semibold">
                                    {getClassifierText(classifiers?.['EXPENSE_TYPE'], expenseTypeCode)}
                                </p>
                            </LinkWithOrigin>
                            {!(description === null || isEmpty(description)) ? (
                                <LinkWithOrigin
                                    className={cn(CELL_LINK, 'whitespace-nowrap')}
                                    to={`/expense/${cell.row.original.id}`}
                                >
                                    <LineClamp className="text-fg-secondary">{description}</LineClamp>
                                </LinkWithOrigin>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                header: t('expenses.grouped.status'),
                id: 'expenseStatusTypeCode',
                accessorKey: 'expenseStatusTypeCode',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'expenseStatusTypeCode',
                    widthWeight: 1,
                    minPx: 122,
                },
                cell: ({ cell }) => {
                    const { expenseStatusTypeCode, timecardId, isNonBillable, isDraft } = cell.row.original;
                    return (
                        <div className="flex flex-row flex-wrap items-center gap-1">
                            {isDraft ? <DraftChip /> : null}
                            {isNonBillable ? (
                                <Chip
                                    className="cursor-pointer"
                                    size="small"
                                    label={<MoneyOffRoundedIcon />}
                                    type={'partyType'}
                                />
                            ) : null}
                            {expenseStatusTypeCode ? (
                                <ExpenseStatusLabel
                                    classifier={classifiers['EXPENSE_STATUS_TYPE']}
                                    statusCode={expenseStatusTypeCode}
                                    isNonBillable={isNonBillable}
                                />
                            ) : null}
                            {timecardId !== null ? (
                                <Tooltip content={t('expenses.opens-timecard')}>
                                    <LinkWithOrigin
                                        className={CELL_LINK}
                                        to={`/timecards/${timecardId}`}
                                        aria-label={t('expenses.opens-timecard')}
                                    >
                                        <TimecardLinkIcon />
                                    </LinkWithOrigin>
                                </Tooltip>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                header: t('expenses.grouped.invoice-paid-date'),
                id: 'invoicePaidDate',
                enableSorting: false,
                meta: {
                    widthWeight: 1.27,
                    // Stacks nowrap invoice-number links over the paid date; 248px is this cell's
                    // measured min-content width at every viewport.
                    minPx: 248,
                },
                cell: ({ cell }) => {
                    const { invoices } = cell.row.original;
                    const paidDate = getLatestConnectedInvoicePaidDate(invoices);
                    return (
                        <div className="flex flex-col gap-0.5">
                            {invoices.length > 0 ? (
                                <div>
                                    {getLinks(
                                        'invoice',
                                        invoices.map(({ id, invoiceNumber }) => ({ id, name: invoiceNumber }))
                                    )}
                                </div>
                            ) : null}
                            {paidDate ? (
                                <span className="text-fg-secondary text-xs">
                                    <FormattedDate value={paidDate} />
                                </span>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                header: t('expenses.added-by'),
                accessorKey: 'employee',
                enableSorting: false,
                meta: {
                    widthWeight: 1.87,
                    minPx: 188,
                },
                cell: ({ cell }) => {
                    const { employee } = cell.row.original;
                    return (
                        <div className="flex flex-row">
                            {employee ? (
                                <RecordLink
                                    className={CELL_LINK}
                                    to={`/employee/${employee.id}`}
                                    label={displayName(employee, lastNameFirst)}
                                    withOrigin
                                />
                            ) : null}
                        </div>
                    );
                },
            },
            {
                header: t('expenses.grouped.case-bd-effort'),
                accessorKey: 'matter',
                enableSorting: false,
                meta: {
                    widthWeight: 2.19,
                    minPx: 144,
                },
                cell: ({ cell }) => {
                    const { matter, effortId } = cell.row.original;
                    return (
                        <div className="flex flex-col gap-1">
                            {matter != null ? (
                                <span className="new:flex-wrap flex items-center gap-1">
                                    <MatterIndicators
                                        isConfidential={!isCaseScoped && matter.isConfidential}
                                        hasAccess={isCaseScoped || hasMatterAccess(matter)}
                                        className={BOTTOM_LINE_IN_NEW_DESIGN}
                                    />
                                    <RecordLink
                                        className={CELL_LINK}
                                        to={`/matter/${matter.id}`}
                                        label={matter.fullName}
                                        withOrigin
                                    />
                                </span>
                            ) : null}
                            {effortId !== null ? (
                                <LinkWithOrigin className={CELL_LINK} to={`/effort/${effortId}`}>
                                    <FormattedMessage id="expenses.bd-effort" />
                                </LinkWithOrigin>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                header: t('expenses.grouped.case-client'),
                id: 'caseClient',
                enableSorting: false,
                meta: {
                    widthWeight: 1.5,
                    minPx: 160,
                },
                cell: ({ cell }) => {
                    // Empty for a BD-effort expense: it carries no case, hence no client.
                    const clientParty = cell.row.original.matter?.clientParty;
                    return clientParty ? (
                        <PartyName party={clientParty} isLink useLinkWithOrigin variant="list" />
                    ) : null;
                },
            },
            {
                header: t('expenses.amount'),
                id: 'expenseAmount',
                accessorKey: 'expenseAmount',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'expenseAmount',
                    widthWeight: 1.25,
                    minPx: 135,
                    renderTotal: renderAmountTotal,
                },
                cell: ({ cell }) => {
                    const { expenseAmount, currencyTypeCode, isDraft } = cell.row.original;
                    // A draft costs nothing yet, and the totals leave it out.
                    return isDraft ? (
                        '—'
                    ) : (
                        <div className="flex flex-row whitespace-nowrap">
                            <MoneyDisplay amount={expenseAmount ?? 0} currency={currencyTypeCode ?? ''} />
                        </div>
                    );
                },
            },
            {
                header: t('expenses.amount-original-currency'),
                id: 'expenseAmountOriginalCurrency',
                accessorKey: 'expenseAmountOriginalCurrency',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'expenseAmountOriginalCurrency',
                    nullHandling: 'nullsLast',
                    widthWeight: 1.25,
                    minPx: 135,
                },
                cell: ({ cell }) => {
                    const { expenseAmountOriginalCurrency, originalCurrencyTypeCode, isDraft } = cell.row.original;
                    return isDraft ? (
                        '—'
                    ) : (
                        <div className="flex flex-row whitespace-nowrap">
                            <MoneyDisplay
                                amount={expenseAmountOriginalCurrency ?? 0}
                                currency={originalCurrencyTypeCode ?? ''}
                            />
                        </div>
                    );
                },
            },
            {
                header: '',
                id: 'actions',
                enableSorting: false,
                meta: {
                    // Single 32px icon button + cell padding. Action columns stay compact rather than
                    // taking a share of the flex pool.
                    fixedPx: 64,
                },
                cell: ({ cell }) => {
                    const expense = cell.row.original;
                    return (
                        <div className="flex justify-end">
                            {expense.isDraft ? (
                                <DraftRowMenu draft={{ type: 'expense', id: expense.id }} />
                            ) : (
                                <ExpenseActionButtons expense={expense} onDuplicate={handleDuplicateExpense} />
                            )}
                        </div>
                    );
                },
            },
        ];
    }, [
        canSelectExpenses,
        isCaseScoped,
        anyToSelect,
        selectedIds,
        finishedItems,
        classifiers,
        handleDuplicateExpense,
        lastNameFirst,
        isSortingEnabled,
    ]);

    const expenseStatusTypeOptions = useMemo(
        () => mapToFilterOptions(EXPENSES_FILTER_STATUS_TYPES, classifiers['EXPENSE_STATUS_TYPE']),
        [classifiers]
    );

    const filterConfig: FilterConfig = useMemo(
        () =>
            buildExpensesFilterConfig({
                classifiers,
                expenseStatusTypeOptions,
                passiveFilter: matter?.id ? `matter.id==${matter.id}` : passiveFilter,
                disabledGroups,
                disabledFilterIds,
                paginatedFieldPassiveFilters,
            }),
        [
            classifiers,
            matter,
            passiveFilter,
            expenseStatusTypeOptions,
            disabledFilterIds,
            disabledGroups,
            paginatedFieldPassiveFilters,
        ]
    );

    const tableActionButtons = () => (
        <ExpensesTableActions
            matterId={matter?.id}
            isMatterNonBillable={matter?.isNonBillable}
            selectedIds={Array.from(selectedIds)}
            onSelectedIdsChange={(ids) => setSelectedIds(new Set(ids))}
            expenses={data?.content ?? []}
        />
    );

    // The div carries the tooltip: a disabled button fires no pointer events.
    const addExpenseButton = matter ? (
        <div>
            <ListCreateButton
                permissions={['CREATE.EXPENSES']}
                variant="contained"
                color="secondary"
                size="small"
                onClick={() => openCreateExpensePage()}
                disabled={
                    isExpenseEntryDisabled || matter.isCurrentlyBlocked || isCaseClosed || isClientPersonaNonGrata
                }
                labelId="expenses.add-new-expense"
                newLabelId="new-entity.expense"
            />
        </div>
    ) : null;

    if (isLoadingClassifiers || isLoadingCurrentEmployeeQueryData || isLoadingTenant) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<ExpenseListItem>
            getRowHref={getExpenseHref}
            listKey="expenses"
            columnConfigId="expenses"
            preservedParamKeys={VIEW_PRESERVED_PARAM_KEYS}
            columns={columns}
            data={data}
            isLoading={isFetching}
            pluralTextId="expenses.expenses"
            singularTextId="expenses.expense"
            fetchData={fetchData}
            fetchCount={useLazyExpensesCountWithDraftsQuery}
            fetchTotals={useGetExpensesTotalsQuery}
            // The aggregation is a single GROUP BY over the filtered set, so it needs neither a
            // record cap nor the deferred opt-in the invoices totals carry — it starts on and the
            // toggle only exists so the row can be dismissed.
            onDemandTotals
            defaultTotalsRowRequested
            filterConfig={filterConfig}
            rowsPerPage={50}
            extraHeaderComponent={
                addExpenseButton ? (
                    addExpenseDisabledTooltipText ? (
                        <Tooltip content={addExpenseDisabledTooltipText}>{addExpenseButton}</Tooltip>
                    ) : (
                        addExpenseButton
                    )
                ) : (
                    (extraHeaderComponent ?? null)
                )
            }
            exportHookResults={
                disableExport || !hasPermissionToExportExpenses
                    ? undefined
                    : [
                          {
                              labelKey: 'export',
                              isBusy: isExportDataFetching,
                              triggerManualDownload,
                          },
                      ]
            }
            renderSearchDescriptionLevelComponent={matter !== undefined ? tableActionButtons : undefined}
            canRememberFilters={canRememberFilters}
            quickFilterTrailing={quickFilterTrailing}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
        />
    );
};

export default ExpensesTable;
